<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';
import type { components } from '@/helpers/api/types/openapi/components.openapi';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref } from 'vue';
import { format } from 'date-fns';

import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import SmsHistoryTableFold from '@/components/tables/SmsHistoryTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<openapi.SMSHistoryContentOpenapi['results']>;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<openapi.SMSHistoryContentOpenapi['results']>>
>;

const openFoldKey = ref<string | null>(null);

function handleFoldClick(key: string): void {
  openFoldKey.value = openFoldKey.value === key ? null : key;
}

const headersData = computed<HeadersData>(function () {
  return [
    { value: 'recipient', title: 'Recipient' },
    { value: 'sent_at', title: 'Sent At' },
    { value: 'sender_name', title: 'Sent By' },
    { value: 'offsiders_list', title: 'Offsider' },
    { value: 'status', title: 'Status' },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function getStatusColor(status?: string): string {
  switch (status) {
    case 'delivered':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

function getRecipientDisplay(
  message: ReadonlyDeep<components['schemas']['SMSHistoryMessage']>,
): string {
  return message.recipient_name || 'Unknown';
}
</script>

<template>
  <div :class="$style.smsHistoryTable" data-testid="smsHistoryTable">
    <table
      :class="[
        tableStyles.table,
        tableStyles.folded,
        tableStyles.hovered,
        $style.smsHistoryTable,
      ]"
    >
      <thead>
        <tr>
          <th></th>
          <SortThTableCell
            v-for="headerItem in headersData"
            :key="headerItem.value"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </tr>
      </thead>
      <tbody v-if="props.loading">
        <tr>
          <td :colspan="foldColspan" class="!h-64 p-0">
            <TableLoader />
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="!sortedData?.length">
        <tr>
          <td :colspan="foldColspan" class="border-b-0 p-0">
            <TableEmptyText />
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <template v-for="message in sortedData" :key="message.id">
          <tr
            :class="[
              tableStyles.foldTitle,
              { [tableStyles.foldOpen]: openFoldKey === message.id.toString() },
            ]"
          >
            <td class="whitespace-nowrap">
              <TableFoldBtn
                :class="tableStyles.foldBtn"
                :is-open="openFoldKey === message.id.toString()"
                @click="handleFoldClick(message.id.toString())"
              />
            </td>
            <td>{{ getRecipientDisplay(message) }}</td>
            <td>
              {{
                message.sent_at
                  ? format(new Date(message.sent_at), 'hh:mm a, MMM d, yyyy')
                  : '-'
              }}
            </td>
            <td>{{ message.sender_name || '-' }}</td>
            <td>{{ message.offsiders_list || '-' }}</td>
            <td>
              <v-chip
                :color="getStatusColor(message.status)"
                size="small"
                variant="flat"
              >
                {{ message.status || 'Unknown' }}
              </v-chip>
            </td>
          </tr>
          <tr
            v-if="openFoldKey === message.id.toString()"
            :class="[
              tableStyles.foldContent,
              { [tableStyles.foldOpen]: openFoldKey === message.id.toString() },
            ]"
          >
            <td :colspan="foldColspan">
              <SmsHistoryTableFold :message="message" />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.smsHistoryTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
</style>
